import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../../config/api';
import {
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface Contact {
  id: number;
  name: string;
  company: string;
  phone: string;
  email: string;
  business_type: string;
  business_type_display: string;
  message?: string;
  status: string;
  status_display: string;
  priority: string;
  priority_display: string;
  assigned_to?: string;
  admin_notes?: string;
  created_at: string;
  updated_at: string;
  first_contact_date?: string;
  last_contact_date?: string;
  next_followup_date?: string;
}

interface ContactEditModalProps {
  contact: Contact | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const ContactEditModal: React.FC<ContactEditModalProps> = ({
  contact,
  isOpen,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    status: '',
    priority: '',
    assigned_to: '',
    admin_notes: '',
    first_contact_date: '',
    last_contact_date: '',
    next_followup_date: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (contact && isOpen) {
      setFormData({
        status: contact.status,
        priority: contact.priority,
        assigned_to: contact.assigned_to || '',
        admin_notes: contact.admin_notes || '',
        first_contact_date: contact.first_contact_date
          ? new Date(contact.first_contact_date).toISOString().slice(0, 16)
          : '',
        last_contact_date: contact.last_contact_date
          ? new Date(contact.last_contact_date).toISOString().slice(0, 16)
          : '',
        next_followup_date: contact.next_followup_date
          ? new Date(contact.next_followup_date).toISOString().slice(0, 16)
          : '',
      });
      setError(null);
      setSuccess(false);
    }
  }, [contact, isOpen]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contact) return;

    setLoading(true);
    setError(null);

    try {
      // 빈 날짜 필드를 null로 변환
      const updateData = {
        ...formData,
        first_contact_date: formData.first_contact_date || null,
        last_contact_date: formData.last_contact_date || null,
        next_followup_date: formData.next_followup_date || null,
      };

      const response = await fetch(
        `${API_BASE_URL}/v1/contacts/${contact.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          },
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '문의 정보 업데이트 실패');
      }

      setSuccess(true);
      setTimeout(() => {
        onSave();
        onClose();
      }, 1000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen || !contact) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* 배경 오버레이 */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        ></div>

        {/* 모달 컨테이너 */}
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
          {/* 헤더 */}
          <div className="flex items-center justify-between pb-4 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                문의 정보 편집
              </h2>
              <p className="text-sm text-gray-500">
                {contact.company} - {contact.name}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* 성공 메시지 */}
          {success && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <CheckIcon className="w-5 h-5 text-green-500 mr-2" />
                <p className="text-green-700">
                  문의 정보가 성공적으로 업데이트되었습니다.
                </p>
              </div>
            </div>
          )}

          {/* 에러 메시지 */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-2" />
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* 폼 */}
          <form onSubmit={handleSubmit} className="mt-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 상태 */}
              <div>
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  상태 *
                </label>
                <select
                  id="status"
                  name="status"
                  required
                  value={formData.status}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:opacity-50"
                >
                  <option value="new">신규 접수</option>
                  <option value="reviewing">검토중</option>
                  <option value="in_progress">상담진행중</option>
                  <option value="demo_scheduled">데모예정</option>
                  <option value="quote_sent">견적발송</option>
                  <option value="negotiating">계약협의</option>
                  <option value="followup_waiting">팔로업대기</option>
                  <option value="completed_success">완료(계약성사)</option>
                  <option value="completed_rejected">완료(거절)</option>
                  <option value="completed_postponed">완료(보류)</option>
                </select>
              </div>

              {/* 우선순위 */}
              <div>
                <label
                  htmlFor="priority"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  우선순위 *
                </label>
                <select
                  id="priority"
                  name="priority"
                  required
                  value={formData.priority}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:opacity-50"
                >
                  <option value="low">낮음</option>
                  <option value="medium">보통</option>
                  <option value="high">높음</option>
                  <option value="urgent">긴급</option>
                </select>
              </div>
            </div>

            {/* 담당자 */}
            <div>
              <label
                htmlFor="assigned_to"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                담당자
              </label>
              <input
                type="text"
                id="assigned_to"
                name="assigned_to"
                value={formData.assigned_to}
                onChange={handleChange}
                disabled={loading}
                placeholder="담당자 이름을 입력하세요"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:opacity-50"
              />
            </div>

            {/* 관리자 메모 */}
            <div>
              <label
                htmlFor="admin_notes"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                관리자 메모
              </label>
              <textarea
                id="admin_notes"
                name="admin_notes"
                rows={4}
                value={formData.admin_notes}
                onChange={handleChange}
                disabled={loading}
                placeholder="내부 메모나 상담 내용을 기록하세요..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:opacity-50"
              />
            </div>

            {/* 일정 관리 */}
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-900">일정 관리</h4>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* 첫 상담일 */}
                <div>
                  <label
                    htmlFor="first_contact_date"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    첫 상담일
                  </label>
                  <input
                    type="datetime-local"
                    id="first_contact_date"
                    name="first_contact_date"
                    value={formData.first_contact_date}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:opacity-50"
                  />
                </div>

                {/* 마지막 상담일 */}
                <div>
                  <label
                    htmlFor="last_contact_date"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    마지막 상담일
                  </label>
                  <input
                    type="datetime-local"
                    id="last_contact_date"
                    name="last_contact_date"
                    value={formData.last_contact_date}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:opacity-50"
                  />
                </div>

                {/* 다음 팔로업 예정일 */}
                <div>
                  <label
                    htmlFor="next_followup_date"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    다음 팔로업 예정일
                  </label>
                  <input
                    type="datetime-local"
                    id="next_followup_date"
                    name="next_followup_date"
                    value={formData.next_followup_date}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:opacity-50"
                  />
                </div>
              </div>
            </div>

            {/* 푸터 버튼 */}
            <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={loading || success}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    저장 중...
                  </div>
                ) : success ? (
                  <div className="flex items-center">
                    <CheckIcon className="w-4 h-4 mr-2" />
                    저장 완료
                  </div>
                ) : (
                  '저장하기'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactEditModal;
